import 'components/ui/dropdowns/custom-dropdown/index.css'
import 'react-select/dist/react-select.css'
import * as React from 'react'
import { AsyncCreatable } from 'react-select'
import InlineSVG from 'svg-inline-react'
import type { DropdownOption } from 'types/ui'
import Popover from 'components/ui/popover'
import Tag from 'components/ui/tag'

function normalize(s: string): string {
  if (typeof s === 'string') {
    return s.toLowerCase()
  } else {
    return s
  }
}

function isOptionUnique({ option, options, labelKey, valueKey }) {
  return (
    options.filter(
      existingOption =>
        normalize(existingOption[labelKey]) === normalize(option[labelKey]) ||
        normalize(existingOption[valueKey]) === normalize(option[valueKey]),
    ).length === 0
  )
}

type Props = {
  onChange: (...args: Array<any>) => any
  value: (Record<string, any> | Record<string, any>[]) | null | undefined
  name?: string
  options: DropdownOption[]
  promptTextCreator?: (...args: Array<any>) => any
  valueComponent: React.ElementType
  optionRenderer?: (...args: Array<any>) => any
  className?: string
  newOptionCreator?: (...args: Array<any>) => any
  multi?: boolean
  clearable?: boolean
  placeholder?: string
  isValidNewOption: (option: DropdownOption) => boolean
  hideArrow?: boolean
  simpleValue?: boolean
  loadOptions: (...args: Array<any>) => any
  disabled?: boolean
}

const CustomDropdown = (props: Props): React.ReactNode => {
  let {
    name,
    options,
    value,
    isValidNewOption,
    multi,
    clearable,
    placeholder,
    hideArrow,
    onChange,
    promptTextCreator,
    valueComponent,
    optionRenderer,
    className,
    simpleValue,
    loadOptions,
    disabled,
  } = props
  const arrowRenderer = hideArrow
    ? () => {}
    : () => (
        <InlineSVG
          src={require('!svg-inline!assets/icons/arrow-down.svg')}
          width="24"
          height="24"
          raw
        />
      )
  let menuRenderer = optionRenderer
    ? undefined
    : props => (
        <Popover
          compact
          items={props.options}
          selected={props.focusedOption}
          onClick={props.selectValue}
        />
      )

  if (!valueComponent) {
    valueComponent = props => {
      const { children, value, onRemove } = props
      return (
        <Tag
          tag={children}
          onRemoveClick={() => {
            onRemove(value)
          }}
        />
      )
    }
  }

  return (
    <AsyncCreatable
      arrowRenderer={arrowRenderer}
      optionRenderer={optionRenderer}
      menuRenderer={menuRenderer}
      valueComponent={multi ? valueComponent : undefined}
      async={true}
      name={name}
      className={className}
      promptTextCreator={promptTextCreator}
      isOptionUnique={isOptionUnique}
      isValidNewOption={isValidNewOption}
      newOptionCreator={props.newOptionCreator}
      multi={multi}
      clearable={!!clearable}
      options={options}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      simpleValue={simpleValue}
      loadOptions={loadOptions}
      filterOptions={options => {
        const clonedOptions = options.slice()
        // without cloning options coult be unselectable
        // https://app.asana.com/0/35126173840535/1141703657432297/f
        return clonedOptions
      }}
      disabled={disabled}
    />
  )
}

export default CustomDropdown
